import { escrow, transfer } from '@portal/config/app.json';
import { DateTime } from 'luxon';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { SettingsActions } from '@portal/actions';
import { VITE_APPLICATION_NAME } from '@portal/config/env';
import {
  AccountTaxIdentifierType,
  DetailType,
  TransactionType,
} from '@portal/models/enumerators';

import { translate } from '@portal/services/i18n';
import { formatPixKey, isCpf, maskCpfOrCnpjHide } from '@portal/services/masks';
import { captalizeString, moneyFormat } from '@portal/utils/format';

import { Tooltip } from '@mui/material';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import { TransactionStatus } from '@portal/enum/approvalsType';
import { TransferType } from '@portal/enum/transfers';
import { TransferTabs } from '@portal/enum/transferTabs';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '@portal/services/message';
import './ConfirmationDetails.less';

interface ConfirmationDetailsProps {
  destiny: models.Contact | models.BilletInfo | models.PixInfo;
  methodType: 'contact' | 'billet';
  value: string;
  transferType?: TransferType;
  transactionScheduleAt?: DateTime;
  setTransactionScheduleAt?: (date: DateTime) => void;
  setTabKey: (tabKey: TransferTabs) => void;
}

const ConfirmationDetails = ({
  destiny,
  transferType,
  value,
  methodType,
  transactionScheduleAt,
  setTabKey,
  setTransactionScheduleAt,
}: ConfirmationDetailsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    user: { me },
    details: { type, transaction },
    paymentAccount: { selectedPaymentAccount },
    settings: { settings },
    theme: { currentTheme },
  } = useReduxState();

  const [tooltipText, setTooltipText] = useState(true);
  const [tariff, setTariff] = useState<number | null>(null);

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    } else if (transferType) {
      setTariff(settings.tariffs[transferType]);
    }
  }, [dispatch, settings, transferType]);

  useEffect(() => {
    if (
      transaction &&
      transaction.status === TransactionStatus.PENDING_APPROVE
    ) {
      navigate(
        `/aprovacao/detalhe-aprovacao/${transaction.transactionRequestPendingApprovalId}`
      );

      return;
    }

    if (
      transaction &&
      type === DetailType.DETAIL &&
      !transfer.hasSmsConfirmation
    ) {
      if (transaction?.status === TransactionStatus.SCHEDULE) {
        navigate(
          `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}${
            transaction?.transactionRequestId
              ? transaction?.transactionRequestId
              : transaction?.id
          }`,
          { state: { isExtract: true, isScheduled: true } }
        );
      }

      if (transaction?.id) {
        navigate(`/inicio/detalhes/${transaction.id}`);
        MessageService.success('PAGES.HOME.APPROVED.TABLE.SUCCESS');
      } else {
        setTabKey(TransferTabs.DETAILS);
      }

      if (transactionScheduleAt) {
        if (
          transactionScheduleAt.diffNow('days').days <= 0 &&
          transaction.type !== TransactionType.PIX
        ) {
          MessageService.success('PAGES.HOME.TRANSFER.SUCCESS_MESSAGE');
        }

        if (transactionScheduleAt.diffNow('days').days > 0) {
          MessageService.success(
            'PAGES.HOME.TRANSFER.SCHEDULED_SUCCESS_MESSAGE'
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, transaction, type]);

  const havePaymentAccount = !!selectedPaymentAccount;

  const isValidContact = (contact: Partial<models.Contact>): boolean => {
    return Boolean(
      contact.bankUserName &&
        contact.bankAgency &&
        contact.bankAccount &&
        contact.bankAccountDigit
    );
  };

  const renderOriginBank = () => {
    const origin = [
      {
        visible: true,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.NAME'),
        value:
          AccountTaxIdentifierType.PERSON === me.account.type
            ? me.name
            : me.account.name,
      },
      {
        visible: me.account.taxIdentifier,
        label: translate(
          `COMPONENTS.CONFIRMATION_DETAILS.TAX_IDENTIFIER.${
            isCpf(me.account.taxIdentifier ?? '') ? 'PERSON' : 'COMPANY'
          }`
        ),
        value: maskCpfOrCnpjHide(me.account.taxIdentifier),
      },
      {
        visible: true,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK'),
        value: VITE_APPLICATION_NAME,
      },
      {
        visible: havePaymentAccount,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK_BRANCH_ACCOUNT'),
        value: '****',
      },
      {
        visible: havePaymentAccount && selectedPaymentAccount.bankAccount,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK_ACCOUNT'),
        value: selectedPaymentAccount?.bankAccount?.replace(/\d/g, '*'),
      },
    ];

    return origin
      .filter((item) => item.visible)
      .map((item) => (
        <div key={item.label} className="confirmation-details__row">
          <p className="confirmation-details__item__bold-text">{item.label}</p>
          <p className="confirmation-details__item__light-text">{item.value}</p>
        </div>
      ));
  };

  const renderDestinationBank = () => {
    const destinyInfo = [
      {
        visible:
          methodType === 'contact' &&
          transaction?.pixInfo?.name &&
          (destiny as models.Contact).alias,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.NICKNAME'),
        value: (destiny as models.Contact).alias,
      },
      {
        visible: true,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.NAME'),
        value:
          methodType === 'contact'
            ? captalizeString(transaction?.pixInfo?.name) ||
              (destiny as models.Contact).alias
            : (destiny as models.BilletInfo).beneficiary?.name,
      },
      {
        visible:
          ((destiny as models.Contact).taxIdentifier ||
            (destiny as models.BilletInfo).beneficiary?.taxIdentifier) &&
          !(
            methodType === 'contact' &&
            (destiny as models.Contact).pixKey &&
            transaction
          ),
        label: translate(
          `COMPONENTS.CONFIRMATION_DETAILS.TAX_IDENTIFIER.${
            isCpf(
              (destiny as models.Contact).taxIdentifier ??
                (destiny as models.BilletInfo).beneficiary?.taxIdentifier ??
                ''
            )
              ? 'PERSON'
              : 'COMPANY'
          }`
        ),
        value: maskCpfOrCnpjHide(
          (destiny as models.Contact).taxIdentifier ??
            (destiny as models.BilletInfo).beneficiary?.taxIdentifier ??
            ''
        ),
      },
      {
        visible:
          (destiny as models.Contact).bankAccount &&
          escrow.enabled &&
          transferType === TransferType.P2P,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK_ACCOUNT'),
        value: `${(destiny as models.Contact).bankAccount}-${
          (destiny as models.Contact).bankAccountDigit
        }`,
      },
      {
        visible:
          methodType === 'contact' &&
          isValidContact(destiny as models.Contact) &&
          !transaction?.pixInfo?.bank,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK'),
        value: (destiny as models.Contact).bankCode
          ? `${(destiny as models.Contact).bankUserName} (${
              (destiny as models.Contact).bankCode
            })`
          : (destiny as models.Contact).bankUserName,
      },
      {
        visible: transferType === TransferType.P2P,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK'),
        value: VITE_APPLICATION_NAME,
      },
      {
        visible:
          methodType === 'contact' &&
          (destiny as models.Contact).pixKey &&
          transaction,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.KEY'),
        value: formatPixKey((destiny as models.Contact).pixKey ?? ''),
      },
      {
        visible:
          methodType === 'contact' &&
          (destiny as models.Contact).pixKey &&
          transaction,
        label: translate(
          `COMPONENTS.CONFIRMATION_DETAILS.TAX_IDENTIFIER.${
            isCpf(transaction?.pixInfo?.taxIdentifier ?? '')
              ? 'PERSON'
              : 'COMPANY'
          }`
        ),
        value: maskCpfOrCnpjHide(transaction?.pixInfo?.taxIdentifier ?? ''),
      },
      {
        visible: transaction?.pixInfo?.bank,
        label: translate('COMPONENTS.CONFIRMATION_DETAILS.BANK'),
        value: transaction?.pixInfo?.bank ?? '',
      },
    ];

    return destinyInfo
      .filter((item) => item.visible)
      .map((item) => (
        <div key={item.label} className="confirmation-details__row">
          <ThemedText
            variant="body"
            className="confirmation-details__item__bold-text"
          >
            {item.label}
          </ThemedText>
          <ThemedText
            variant="body"
            className="confirmation-details__item__light-text"
          >
            {item.value}
          </ThemedText>
        </div>
      ));
  };

  return (
    <>
      <div className="confirmation-details__header">
        <div>
          {transferType ? (
            <div
              className="confirmation-details__header__type"
              style={{ background: currentTheme.background }}
            >
              {translate(
                `COMPONENTS.CONFIRMATION_DETAILS.TRANSACTION_TYPES.${transferType?.toUpperCase()}`
              )}
            </div>
          ) : null}
          <div className="confirmation-details__header__amount">{value}</div>
          {typeof tariff === 'number' && tariff !== 0 && (
            <>
              <ThemedText variant="body">
                {translate('COMPONENTS.CONFIRMATION_DETAILS.TARIFF_VALUE')}
              </ThemedText>
              <ThemedText variant="body">{moneyFormat(tariff)}</ThemedText>
            </>
          )}
        </div>
        <div>
          <ThemedText
            variant="body"
            className="confirmation-details__item__bold-text"
          >
            {translate('COMPONENTS.CONFIRMATION_DETAILS.TRANSACTION_DATE')}
          </ThemedText>
          <Tooltip
            title={
              tooltipText && transferType === TransferType.PIX
                ? translate(
                    'COMPONENTS.CONFIRMATION_DETAILS.SCHEDULE_TRANSACTION'
                  )
                : ''
            }
          >
            <div
              onClick={() => setTooltipText(false)}
              onMouseLeave={() => setTooltipText(true)}
            >
              <AdvancedDateTimePicker
                onlyDate
                disablePast
                value={
                  transferType !== 'pix' && transferType !== 'p2p'
                    ? DateTime.local().toISODate()
                    : transactionScheduleAt?.toISODate()
                }
                onChange={(value) => {
                  if (transferType !== 'pix' && transferType !== 'p2p') {
                    return;
                  }
                  if (setTransactionScheduleAt && value) {
                    setTransactionScheduleAt(value);
                  }
                }}
                disabled={transferType !== 'pix' && transferType !== 'p2p'}
              />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="confirmation-details">
        <AdvancedCard testId="confirmation-origin-card">
          <ThemedText variant="h3">
            {translate('COMPONENTS.CONFIRMATION_DETAILS.BANK_ORIGIN')}
          </ThemedText>
          {renderOriginBank()}
        </AdvancedCard>
        <AdvancedCard testId="confirmation-destination-card">
          <ThemedText variant="h3">
            {translate('COMPONENTS.CONFIRMATION_DETAILS.DESTINATION_BANK')}
          </ThemedText>
          {renderDestinationBank()}
        </AdvancedCard>
      </div>
    </>
  );
};

export default ConfirmationDetails;
